"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProductResolver = exports.KeyProductMnemonicIdResolver = void 0;
const constants_1 = require("root/src/shared/rewards/constants");
const dropKeys_1 = require("root/src/shared/constants/dropKeys");
const stringCase_1 = require("root/src/shared/util/stringCase");
const isProductType = (prefix) => {
    return prefix === 'dropCode' || prefix === 'dropKey';
};
class KeyProductResolver {
    constructor(sortKey) {
        const [, state, dropGameId, keyPlatform, keyRegion, keyType, encryptData] = sortKey.split('|');
        if (!state || !dropGameId || !keyPlatform || !keyRegion || !keyType || !encryptData) {
            throw new Error(`Invalid sortKey: ${sortKey}`, { cause: sortKey });
        }
        this._state = state;
        this._platform = keyPlatform;
        this._region = keyRegion;
        this._type = keyType;
        this._dropGameId = dropGameId;
        this._encryptData = encryptData;
    }
    get state() {
        return this._state;
    }
    get platform() {
        return this._platform;
    }
    get region() {
        return this._region;
    }
    get type() {
        return this._type;
    }
    get dropGameId() {
        return this._dropGameId;
    }
    get sortKey() {
        return `${this.state}|${this.dropGameId}|${this.platform}|${this.region}|${this.type}`;
    }
    get productId() {
        return `${this.dropGameId}|${this.platform}|${this.region}|${this.type}`;
    }
    get encryptedData() {
        return this._encryptData;
    }
}
class KeyProductMnemonicIdResolver {
    constructor(mnemonicId) {
        const [dropGameId, platform, region, type] = mnemonicId.split('|');
        if (!dropGameId || !platform || !region || !type) {
            throw new Error(`Invalid mnemonicId: ${mnemonicId}`, { cause: mnemonicId });
        }
        this._region = region;
        this._platform = platform;
        this._type = type;
        this._dropGameId = dropGameId;
    }
    get region() {
        return this._region;
    }
    get type() {
        return this._type;
    }
    get dropGameId() {
        return this._dropGameId;
    }
    get platform() {
        return this._platform;
    }
    getReadablePlatform() {
        return dropKeys_1.readablePlatformsMap[this.platform];
    }
    getReadableKeyType() {
        return dropKeys_1.readableKeyTypesMap[this._type];
    }
    getReadableRegion() {
        const option = dropKeys_1.dropKeyPlatformRegionsOptionsMap[this.platform];
        const regionOption = option === null || option === void 0 ? void 0 : option.find(({ value }) => value === this.region);
        if (!regionOption) {
            return (0, stringCase_1.capitalize)(this.region);
        }
        return regionOption.label;
    }
}
exports.KeyProductMnemonicIdResolver = KeyProductMnemonicIdResolver;
class CodeProductResolver {
    constructor(sortKey) {
        const [, state, codeId, encryptedData] = sortKey.split('|');
        if (!state || !codeId || !encryptedData) {
            throw new Error('Invalid sortKey', { cause: sortKey });
        }
        this._state = state;
        this._codeId = codeId;
        this._encryptedData = encryptedData;
    }
    get state() {
        return this._state;
    }
    get codeId() {
        return this._codeId;
    }
    get sortKey() {
        return `${this.state}|${this.codeId}`;
    }
    get productId() {
        return this.codeId;
    }
    get encryptedData() {
        return this._encryptedData;
    }
}
class ProductResolver {
    static resolveState(sortKey) {
        const prefix = sortKey.split('|').at(0);
        if (!isProductType(prefix)) {
            return undefined;
        }
        switch (prefix) {
            case constants_1.RewardType.KEY:
                return new KeyProductResolver(sortKey).state;
            case constants_1.RewardType.CODE:
                return new CodeProductResolver(sortKey).state;
            default:
                return undefined;
        }
    }
    static resolveProductId(sortKey) {
        const prefix = sortKey.split('|').at(0);
        if (!isProductType(prefix)) {
            return undefined;
        }
        switch (prefix) {
            case constants_1.RewardType.KEY:
                return new KeyProductResolver(sortKey).productId;
            case constants_1.RewardType.CODE:
                return new CodeProductResolver(sortKey).productId;
            default:
                return undefined;
        }
    }
    static resolveType(sortKey) {
        const prefix = sortKey.split('|').at(0);
        if (!isProductType(prefix)) {
            return undefined;
        }
        return prefix;
    }
    static resolveEncryptedData(sortKey) {
        const prefix = sortKey.split('|').at(0);
        if (!isProductType(prefix)) {
            return undefined;
        }
        switch (prefix) {
            case constants_1.RewardType.KEY:
                return new KeyProductResolver(sortKey).encryptedData;
            case constants_1.RewardType.CODE:
                return new CodeProductResolver(sortKey).encryptedData;
            default:
                return undefined;
        }
    }
    static resolveRegionByMnemonicId(mnemonicId, type) {
        if (!isProductType(type)) {
            return undefined;
        }
        switch (type) {
            case constants_1.RewardType.KEY:
                return new KeyProductMnemonicIdResolver(mnemonicId).region;
            default:
                return undefined;
        }
    }
    static resolvePlatformByMnemonicId(mnemonicId, type) {
        if (!isProductType(type)) {
            return undefined;
        }
        switch (type) {
            case constants_1.RewardType.KEY:
                return new KeyProductMnemonicIdResolver(mnemonicId).platform;
            default:
                return undefined;
        }
    }
}
exports.ProductResolver = ProductResolver;
